import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  ViewChild,
} from '@angular/core';
import { Table, TableModule } from 'primeng/table';
import { TableBaseAbstractComponent } from '../../../../shared/components/table-base-abstract.component';
import { CzlonekRodziny } from '../../../../shared/models/czlonek-rodziny';
import { FamilyMemberValidationFormBuilder } from './form-group/family-member-validation.form.builder';
import { catchError, EMPTY, filter, finalize, Observable, tap } from 'rxjs';
import { UserApi } from '../../../../shared/services/api/user.api';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { PreviewDataWrapperComponent } from '../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { PreviewDataType } from '../../../../shared/enums/preview-data-type.enum';
import { TableRequestData } from '../../../../shared/models/table/table-request-param';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../../core/config/app-paths';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { TableResponse } from '../../../../shared/models/table-response';
import { SortDirection } from '../../../../shared/enums/sort-direction.enum';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Paginator } from 'primeng/paginator';
import { TooltipModule } from 'primeng/tooltip';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';

@Component({
  selector: 'app-family-members-table',
  imports: [
    TableModule,
    DatePipe,
    NgTemplateOutlet,
    Button,
    Card,
    PreviewDataWrapperComponent,
    Paginator,
    TooltipModule,
  ],
  templateUrl: './family-members-table.component.html',
  styleUrl: './family-members-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FamilyMembersTableComponent extends TableBaseAbstractComponent<CzlonekRodziny> {
  readonly #userApi = inject(UserApi);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dialogService = inject(DialogService);

  @ViewChild('table') override tableRef!: Table;

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<CzlonekRodziny>> => {
    params.sortBy = !params.sortBy ? 'imie' : params.sortBy;
    params.order = !params.order ? SortDirection.ASC : params.order;
    return this.#userApi.getFamilyMembers$(params);
  };
  protected readonly PreviewDataType = PreviewDataType;
  protected readonly validationMessage =
    'Dane członka rodziny są niepoprawne. Popraw je, edytując dane tej osoby.';
  protected readonly validatedRows = computed(() =>
    this.rows().map((member) => ({
      ...member,
      hasValidationErrors:
        FamilyMemberValidationFormBuilder.createFormGroup(member).invalid,
    })),
  );

  protected navigateToEditFamilyMember(id: number): void {
    const url = `${APP_PATHS.userSettings.root}/${APP_PATHS.userSettings.editFamilyMember.replace(':id', id.toString())}`;
    this.#router.navigate([url]);
  }

  protected deleteFamilyMemberDialog(user: CzlonekRodziny): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz usunąć członka rodziny: ${user.imie} ${user.nazwisko}`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#deleteFamilyMember(user.id!)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #deleteFamilyMember(userId: number): void {
    this.#spinnerService.show();
    this.#userApi
      .deleteFamilyMember$(userId)
      .pipe(
        tap(() => {
          this.reloadData();
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie usunięto członka rodziny',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas pobierania danych',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => this.#spinnerService.hide()),
      )
      .subscribe();
  }

  protected createFamilyMember(): void {
    const url = `${APP_PATHS.userSettings.root}/${APP_PATHS.userSettings.addFamilyMember}`;
    this.#router.navigate([url]);
  }

  protected goToHomepage(): void {
    this.#router.navigate(['/']);
  }
}
